// Minimal Server-Sent-Events consumer over fetch + ReadableStream.
//
// Browser EventSource doesn't support POST or custom headers. So we
// open the SSE endpoint with fetch() and parse the SSE wire format
// (`event: <name>\n` + `data: <json>\n\n`) ourselves.
//
// Usage:
//   await consumeSSE("/skills/1/ai_chat", { method: "POST", body: ... }, {
//     onText: (delta) => …,
//     onToolProposal: (proposal) => …,
//     onEnd: () => …,
//     onError: (msg) => …,
//   })

export type SseHandlers = {
  onText?: (delta: string) => void
  onToolProposal?: (proposal: unknown) => void
  onPing?: () => void
  onEnd?: () => void
  onError?: (msg: string) => void
  /** Optional catch-all: receives every parsed event (event-name + data). */
  onEvent?: (event: string, data: unknown) => void
}

export async function consumeSSE(
  url: string,
  init: RequestInit,
  handlers: SseHandlers,
): Promise<void> {
  const headers = new Headers(init.headers)
  headers.set("Accept", "text/event-stream")
  // Always include the CSRF token for POST/PATCH.
  const token = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content
  if (token) headers.set("X-CSRF-Token", token)

  let res: Response
  try {
    res = await fetch(url, {
      ...init,
      headers,
      credentials: "same-origin",
    })
  } catch (e) {
    handlers.onError?.((e as Error).message)
    return
  }

  if (!res.ok || !res.body) {
    handlers.onError?.(`SSE HTTP ${res.status}`)
    return
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""

  try {
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      // SSE messages are separated by a blank line ("\n\n").
      let sepIndex: number
      while ((sepIndex = buffer.indexOf("\n\n")) !== -1) {
        const raw = buffer.slice(0, sepIndex)
        buffer = buffer.slice(sepIndex + 2)
        const parsed = parseSseFrame(raw)
        if (parsed) dispatch(parsed.event, parsed.data, handlers)
      }
    }
  } finally {
    reader.releaseLock?.()
  }
}

function parseSseFrame(raw: string): { event: string; data: unknown } | null {
  let event = "message"
  const dataLines: string[] = []
  for (const line of raw.split("\n")) {
    if (line.startsWith(":")) continue            // SSE comment
    if (line.startsWith("event:")) {
      event = line.slice(6).trim()
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trim())
    }
  }
  if (dataLines.length === 0) return null
  const dataStr = dataLines.join("\n")
  let data: unknown = dataStr
  try {
    data = JSON.parse(dataStr)
  } catch {
    /* leave as string */
  }
  return { event, data }
}

function dispatch(event: string, data: unknown, h: SseHandlers) {
  h.onEvent?.(event, data)
  switch (event) {
    case "text": {
      const delta = (data as { delta?: string })?.delta ?? ""
      if (delta) h.onText?.(delta)
      break
    }
    case "tool_proposal":
      h.onToolProposal?.(data)
      break
    case "ping":
      h.onPing?.()
      break
    case "end":
      h.onEnd?.()
      break
    case "error": {
      const msg = (data as { message?: string })?.message ?? "stream error"
      h.onError?.(msg)
      break
    }
  }
}
